// Compat layer per react-router v5 → v7.
//
// Permette ai 77 file legacy che usano `useHistory` / `withRouter` /
// `match.params` di continuare a funzionare senza modifiche, mappando
// le API v5 sulle nuove di v7. La conversione a hooks puri (useNavigate,
// useParams, useLocation) avviene progressivamente in PR successive
// (Fase 5 del piano), una feature alla volta.
//
// IMPORTANTE: questi shim non implementano TUTTO il contratto v5 —
// coprono i pattern usati nel codebase:
// - history.push(path) | history.push({pathname, state})
// - history.replace(path) | history.replace({pathname, state})
// - history.goBack()
// - withRouter inietta { history, location, match } come props
// - match.params (via useParams)

import React, { useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';

// Bridge per chiamare `navigate` da fuori dei componenti React (programmatic
// navigation in moduli "utility"). Settato dall'App tramite `<RouterBridge />`
// che chiama `setRouterNavigate(navigate)` allo mount.
let _navigate = null;
export function setRouterNavigate(nav) {
  _navigate = nav;
}
export function rrNavigate(to, options) {
  if (_navigate) _navigate(to, options);
  else console.warn('rrNavigate chiamato prima del bootstrap del Router');
}

/**
 * Componente da renderizzare DENTRO il <BrowserRouter> per agganciare
 * il navigate al bridge globale.
 */
export function RouterBridge() {
  const navigate = useNavigate();
  useEffect(() => {
    setRouterNavigate(navigate);
    return () => setRouterNavigate(null);
  }, [navigate]);
  return null;
}

/**
 * Costruisce un oggetto history-compat v5 a partire da useNavigate/useLocation.
 */
function buildHistoryShim(navigate, location) {
  const go = (to, options) => {
    if (typeof to === 'string') {
      navigate(to, options);
    } else if (to && typeof to === 'object') {
      const { pathname, state, search, hash } = to;
      navigate({ pathname, search, hash }, { ...options, state });
    }
  };
  return {
    push: (to) => go(to),
    replace: (to) => go(to, { replace: true }),
    goBack: () => navigate(-1),
    goForward: () => navigate(1),
    go: (n) => navigate(n),
    location,
  };
}

/**
 * Hook v5-compat. Ritorna un oggetto con `push`, `replace`, `goBack`, ecc.
 * @returns {ReturnType<typeof buildHistoryShim>}
 */
export function useHistory() {
  const navigate = useNavigate();
  const location = useLocation();
  return React.useMemo(() => buildHistoryShim(navigate, location), [navigate, location]);
}

/**
 * HOC v5-compat. Inietta `history`, `location`, `match` come props.
 * Da usare per i class component legacy che non possono chiamare hooks.
 */
export function withRouter(Component) {
  const Wrapped = (props) => {
    const navigate = useNavigate();
    const location = useLocation();
    const params = useParams();
    const history = React.useMemo(() => buildHistoryShim(navigate, location), [navigate, location]);
    const match = { params, isExact: true, path: location.pathname, url: location.pathname };
    return <Component {...props} history={history} location={location} match={match} />;
  };
  Wrapped.displayName = `withRouter(${Component.displayName || Component.name || 'Component'})`;
  return Wrapped;
}
